import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { ChevronDown, ChevronRight, Copy as CopyIcon, Download, FileText, ListChecks, RotateCcw, X } from "lucide-react"

import { useLicenseAssets, type AssetOption, type DownloadFormat } from "@/hooks/useLicenseAssets"
import { useLicenseData } from "@/hooks/useLicenseData"
import { useUIStore, type UISection } from "@/hooks/useUIStore"
import { useWidgetTop } from "@/hooks/useWidgetTop"
import { copyContent } from "@/lib/copyContent"
import { type LicenseLogo } from "@/lib/getLicenseLogos"
import { EMBED_SCRIPT_URL } from "@/lib/generateEmbedSnippet"
import type { LicenseIssue } from "@/lib/licenseValidation"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion"
import { Button } from "../ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu"
import { LicenseLogoBar } from "./LicenseLogoBar"
import { EmbedSection } from "./widget/EmbedSection"
import { JsonSection } from "./widget/JsonSection"
import { PlainLanguageSection } from "./widget/PlainLanguageSection"

type WidgetSection = {
  id: UISection
  title: string
  body: JSX.Element
}

type WidgetMenu = {
  id: "download" | "copy"
  label: string
  icon: typeof Download
  options: AssetOption[]
  action: (key: DownloadFormat) => void | Promise<void>
}

type WidgetHeaderProps = {
  logos: LicenseLogo[]
  licenseName: string
  licenseCode?: string
  widgetDownloadOptions: AssetOption[]
  onWidgetDownload: (key: DownloadFormat) => void | Promise<void>
}

const WidgetHeader = memo(({ logos, licenseName, licenseCode, widgetDownloadOptions, onWidgetDownload }: WidgetHeaderProps) => (
  <div className="flex flex-col gap-1.5">
    <h2 className="text-lg font-semibold leading-tight text-foreground">Your License</h2>
    <LicenseLogoBar
      logos={logos}
      licenseName={licenseName}
      licenseCode={licenseCode}
      downloadOptions={widgetDownloadOptions}
      onDownload={onWidgetDownload}
    />
  </div>
))

type WidgetContentProps = {
  logos: LicenseLogo[]
  licenseName: string
  licenseCode?: string
  baseBorder: string
  accordion: UISection | null
  setAccordion: (value: UISection | null) => void
  sections: WidgetSection[]
  menus: WidgetMenu[]
  onReset: () => void
  widgetDownloadOptions: AssetOption[]
  onWidgetDownload: (key: DownloadFormat) => void | Promise<void>
  allowContentStretch?: boolean
}

type WidgetIssuesProps = {
  issues: LicenseIssue[]
  baseBorder: string
  onIssueClick: (field: string) => void
}

const WidgetContent = ({
  logos,
  licenseName,
  licenseCode,
  baseBorder,
  accordion,
  setAccordion,
  sections,
  menus,
  onReset,
  widgetDownloadOptions,
  onWidgetDownload,
  allowContentStretch = false
}: WidgetContentProps) => (
  <>
    <WidgetHeader
      logos={logos}
      licenseName={licenseName}
      licenseCode={licenseCode}
      widgetDownloadOptions={widgetDownloadOptions}
      onWidgetDownload={onWidgetDownload}
    />

    <div
      className={
        allowContentStretch
          ? "pr-1 flex flex-col gap-2"
          : "flex-1 pr-1 flex flex-col gap-2 overflow-hidden min-h-0"
      }
    >
      <Accordion
        type="single"
        value={accordion ?? "plain"}
        onValueChange={value => setAccordion((value as UISection) ?? "plain")}
        className={`divide-y divide-slate-200 rounded-xl bg-white/70 ${baseBorder} flex flex-col ${
          allowContentStretch ? "" : "h-full min-h-0 overflow-hidden"
        }`}
      >
        {sections.map(section => (
          <AccordionItem
            key={section.id}
            value={section.id}
            className={`flex flex-col min-w-0 ${allowContentStretch ? "" : "min-h-0 data-[state=open]:flex-1"}`}
          >
            <AccordionTrigger className="px-5">{section.title}</AccordionTrigger>
            <AccordionContent
              className={
                allowContentStretch
                  ? "data-[state=open]:block"
                  : "data-[state=open]:flex data-[state=open]:flex-1 data-[state=open]:min-h-0 data-[state=open]:min-w-0 data-[state=open]:overflow-hidden h-full"
              }
              innerClassName={allowContentStretch ? "" : "flex-1 min-w-0"}
            >
              <div
                className={
                  allowContentStretch
                    ? "min-w-0 px-5 pt-0 pb-4"
                    : "flex-1 min-h-0 min-w-0 h-full overflow-auto px-5 pt-0 pb-4 flex-1"
                }
              >
                {section.body}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>

    <div className="flex flex-wrap items-center justify-end gap-3 rounded-xl bg-slate-50/80 p-3 z-[500]">
      {menus.map(menu => (
        <DropdownMenu key={menu.id}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className={
                menu.id === "download"
                  ? "gap-2 px-3 border-emerald-600 bg-emerald-600 text-white hover:border-emerald-700 hover:bg-emerald-700 hover:text-white"
                  : menu.id === "copy"
                    ? "gap-2 px-3 border-emerald-600 text-emerald-700 hover:border-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                    : "gap-2 px-3"
              }
            >
              <menu.icon className="size-4" />
              {menu.label}
              <ChevronDown
                className={
                  menu.id === "download"
                    ? "size-4 text-white/80"
                    : menu.id === "copy"
                      ? "size-4 text-emerald-700"
                      : "size-4 text-muted-foreground"
                }
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {menu.options.map(option => (
              <DropdownMenuItem
                key={option.key}
                onSelect={event => {
                  event.preventDefault()
                  menu.action(option.key)
                }}
              >
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ))}
      <Button variant="destructive" className="gap-2 px-3" onClick={onReset}>
        <RotateCcw className="size-4" />
        Clear
      </Button>
    </div>
  </>
)

const WidgetIssues = ({ issues, baseBorder, onIssueClick }: WidgetIssuesProps) => {
  const count = issues.length
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4" data-widget-issues-header>
        <div className="flex items-start gap-3">
          <div className="flex size-10 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
            <ListChecks className="size-5" />
          </div>
          <div className="space-y-1">
            <h2 className="text-lg font-semibold leading-tight text-foreground">Checklist</h2>
            <p className="text-sm text-muted-foreground">Work through the checklist to generate your license.</p>
          </div>
        </div>
        <span className="inline-flex size-8 items-center justify-center rounded-full bg-emerald-600 text-xs font-semibold text-white">
          {count}
        </span>
      </div>
      <div className={`overflow-hidden rounded-xl bg-white/70 ${baseBorder}`}>
        <ol className="flex flex-col divide-y divide-slate-200 overflow-auto" data-widget-issues-list>
          {issues.map((issue, index) => (
            <li key={`${issue.field}-${index}`} className="p-4">
              <button
                type="button"
                onClick={() => onIssueClick(issue.field)}
                className="group flex w-full items-center justify-between gap-4 rounded-lg border border-transparent px-3 py-2 text-left text-sm transition hover:border-slate-200 hover:bg-slate-50 focus-visible:border-slate-300 focus-visible:outline-hidden"
              >
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex size-6 items-center justify-center rounded-full bg-rose-100 text-xs font-semibold text-rose-600">
                    {index + 1}
                  </span>
                  <span className="font-medium text-slate-800">{issue.message}</span>
                </div>
                <ChevronRight className="size-4 text-muted-foreground transition group-hover:translate-x-0.5" />
              </button>
            </li>
          ))}
        </ol>
      </div>
    </div>
  )
}

export const Widget = ({ visible, onReset }: { visible: boolean; onReset: () => void }) => {
  const { top, documentTop, measured: widgetPositionMeasured } = useWidgetTop()
  const accordion = useUIStore(state => state.accordion)
  const setAccordion = useUIStore(state => state.setAccordion)
  const { rawData, markdown, exportMarkdown, logos, licenseName, licenseFullCode, issues } =
    useLicenseData()
  const [embedTheme, setEmbedTheme] = useState<"light" | "dark" | "auto">("light")
  const [embedSize, setEmbedSize] = useState<"sm" | "md" | "lg">("sm")
  const [systemPrefersDark, setSystemPrefersDark] = useState(false)
  const { json, embedHtml, isEmbedReady, download, copyAsset, downloadOptions, copyOptions, widgetDownloadOptions } =
    useLicenseAssets({
      markdown,
      exportMarkdown,
      rawData,
      logos,
      licenseName,
      licenseCode: licenseFullCode,
      embedTheme,
      embedSize
    })

  const baseBorder = "border border-slate-200"
  const embedFrameRef = useRef<HTMLIFrameElement>(null)
  const [embedFrameSize, setEmbedFrameSize] = useState({ width: 0, height: 0 })
  const embedFrameSizeRef = useRef({ width: 0, height: 0 })
  const previewPadding = 16
  const embedPreviewHeight = useMemo(() => {
    if (embedSize === "lg") return 320
    if (embedSize === "md") return 280
    return 240
  }, [embedSize])
  const updateEmbedFrameSize = useCallback(() => {
    const frame = embedFrameRef.current
    if (!frame?.contentDocument) return
    const doc = frame.contentDocument
    const card = doc.querySelector(".dlb-card") as HTMLElement | null
    const root = doc.querySelector(".adbex-license-widget") as HTMLElement | null
    const target = card ?? root
    let width = 0
    let height = 0
    if (target) {
      const rect = target.getBoundingClientRect()
      width = Math.ceil(rect.width)
      height = Math.ceil(rect.height)
    } else {
      const { documentElement, body } = doc
      width = Math.ceil(Math.max(documentElement?.scrollWidth, body?.scrollWidth ?? 0))
      height = Math.ceil(Math.max(documentElement?.scrollHeight, body?.scrollHeight ?? 0))
    }
    const safePadding = 2
    width = Math.max(0, width + safePadding)
    height = Math.max(0, height + safePadding)
    const nextWidth = Math.round(width)
    const nextHeight = Math.round(height)
    const prev = embedFrameSizeRef.current
    const deltaW = Math.abs(prev.width - nextWidth)
    const deltaH = Math.abs(prev.height - nextHeight)
    if (deltaW <= 1 && deltaH <= 1) return
    embedFrameSizeRef.current = { width: nextWidth, height: nextHeight }
    setEmbedFrameSize(prevSize =>
      prevSize.width === nextWidth && prevSize.height === nextHeight
        ? prevSize
        : { width: nextWidth, height: nextHeight }
    )
  }, [])
  useEffect(() => {
    if (typeof window === "undefined") return
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== embedFrameRef.current?.contentWindow) return
      const data = event.data as { type?: string; width?: number; height?: number } | null
      if (!data || data.type !== "AdbexEmbedSize") return
      if (typeof data.width !== "number" || typeof data.height !== "number") return
      const nextWidth = Math.round(data.width)
      const nextHeight = Math.round(data.height)
      const prev = embedFrameSizeRef.current
      const deltaW = Math.abs(prev.width - nextWidth)
      const deltaH = Math.abs(prev.height - nextHeight)
      if (deltaW <= 1 && deltaH <= 1) return
      embedFrameSizeRef.current = { width: nextWidth, height: nextHeight }
      setEmbedFrameSize(prevSize =>
        prevSize.width === nextWidth && prevSize.height === nextHeight
          ? prevSize
          : { width: nextWidth, height: nextHeight }
      )
    }
    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [])
  const handleEmbedFrameLoad = useCallback(() => {
    updateEmbedFrameSize()
    window.setTimeout(updateEmbedFrameSize, 250)
  }, [updateEmbedFrameSize])
  const embedPreviewHtml = useMemo(() => {
    if (!isEmbedReady) return ""
    const adjusted = embedHtml
      .replace(EMBED_SCRIPT_URL, "/embed/license-widget.min.js")
      .replace("<script async", "<script")
    return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <style>
      html, body { margin: 0; padding: 0; background: transparent; width: max-content; height: auto; }
      body { display: inline-block; }
      .adbex-license-widget { width: max-content; }
      .adbex-license-widget[data-theme="dark"],
      .adbex-license-widget[data-theme="auto"] { --dlb-card-bg: #141416; }
    </style>
  </head>
  <body>${adjusted}
    <script>
      (function() {
        var sendSize = function() {
          var card = document.querySelector(".dlb-card");
          var widget = document.querySelector(".adbex-license-widget");
          var target = card || widget;
          if (!target) return;
          var rect = target.getBoundingClientRect();
          var padding = 10;
          var width = Math.ceil(rect.width) + padding;
          var height = Math.ceil(rect.height) + padding;
          if (window.parent && window.parent !== window) {
            window.parent.postMessage({ type: "AdbexEmbedSize", width: width, height: height }, "*");
          }
        };
        var schedule = function() {
          if (window.requestAnimationFrame) {
            window.requestAnimationFrame(sendSize);
          } else {
            setTimeout(sendSize, 0);
          }
        };
        window.addEventListener("load", schedule);
        var observer = new MutationObserver(schedule);
        observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true });
        if (window.ResizeObserver) {
          var resizeTarget = document.querySelector(".dlb-card") || document.querySelector(".adbex-license-widget");
          if (resizeTarget) {
            var resizeObserver = new ResizeObserver(schedule);
            resizeObserver.observe(resizeTarget);
          }
        }
        setTimeout(schedule, 300);
      })();
    </script>
  </body>
</html>`
  }, [embedHtml, isEmbedReady])
  useEffect(() => {
    embedFrameSizeRef.current = { width: 0, height: 0 }
    setEmbedFrameSize({ width: 0, height: 0 })
  }, [embedPreviewHtml])
  const handleEmbedCopy = useCallback(async () => {
    if (!embedHtml) return
    await copyContent(embedHtml)
  }, [embedHtml])
  const sections: WidgetSection[] = [
    {
      id: "plain",
      title: "Plain Language",
      body: <PlainLanguageSection markdown={markdown} />
    },
    {
      id: "embed",
      title: "HTML Embed",
      body: (
        <EmbedSection
          baseBorder={baseBorder}
          embedTheme={embedTheme}
          systemPrefersDark={systemPrefersDark}
          embedFrameSize={embedFrameSize}
          previewPadding={previewPadding}
          embedPreviewHeight={embedPreviewHeight}
          isEmbedReady={isEmbedReady}
          hasLogos={logos.length > 0}
          embedFrameRef={embedFrameRef}
          embedPreviewHtml={embedPreviewHtml}
          handleEmbedFrameLoad={handleEmbedFrameLoad}
          embedSize={embedSize}
          setEmbedTheme={setEmbedTheme}
          setEmbedSize={setEmbedSize}
          embedHtml={embedHtml}
          handleEmbedCopy={handleEmbedCopy}
        />
      )
    },
    {
      id: "json",
      title: "JSON Data",
      body: <JsonSection json={json} />
    }
  ]

  const menus: WidgetMenu[] = [
    { id: "download", label: "Download", icon: Download, options: downloadOptions, action: download },
    { id: "copy", label: "Copy", icon: CopyIcon, options: copyOptions, action: copyAsset }
  ]

  const [mobileOpen, setMobileOpen] = useState(false)
  const [desktopHeight, setDesktopHeight] = useState<number | null>(null)
  const [desktopAvailable, setDesktopAvailable] = useState<number | null>(null)
  const [desktopInView, setDesktopInView] = useState(true)
  const [minChecklistHeight, setMinChecklistHeight] = useState(600)
  const desktopWidgetRef = useRef<HTMLDivElement>(null)
  const topRef = useRef(top)
  const initialTopRef = useRef(top)
  const recomputedAfterTopChange = useRef(false)
  const unstickTopRef = useRef<number | null>(null)
  const footerOffsetRef = useRef(0)
  const STICKY_TOP = 56 + 24
  const BASE_BOTTOM_GAP = 24
  const FOOTER_PUSH_MAX = 92
  const issueCount = issues.length
  const minDesktopHeight = issueCount > 0 ? Math.max(600, minChecklistHeight) : 900
  const isStickyReady = top <= STICKY_TOP + 0.5
  const needsDesktopScroll = desktopAvailable !== null && desktopAvailable < minDesktopHeight && isStickyReady
  const desktopTop = needsDesktopScroll ? (unstickTopRef.current ?? documentTop) : top

  useEffect(() => {
    if (issueCount === 0) {
      setMinChecklistHeight(600)
      return
    }
    if (typeof window === "undefined") return
    const container = desktopWidgetRef.current
    if (!container) return
    const header = container.querySelector<HTMLElement>("[data-widget-issues-header]")
    const list = container.querySelector<HTMLElement>("[data-widget-issues-list]")
    if (!header || !list) return
    let raf = 0
    const containerPadding = 48
    const gap = 16

    const measure = () => {
      const headerHeight = header.getBoundingClientRect().height
      const items = Array.from(list.querySelectorAll("li"))
      const listHeight =
        items.length > 0
          ? items.reduce((total, item) => total + item.getBoundingClientRect().height, 0)
          : list.scrollHeight
      const next = Math.ceil(headerHeight + gap + listHeight + containerPadding)
      setMinChecklistHeight(prev => (Math.abs(prev - next) > 1 ? next : prev))
    }

    const schedule = () => {
      if (raf) return
      raf = window.requestAnimationFrame(() => {
        raf = 0
        measure()
      })
    }

    measure()
    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null
    resizeObserver?.observe(header)
    resizeObserver?.observe(list)
    const mutationObserver = new MutationObserver(schedule)
    mutationObserver.observe(list, { childList: true, subtree: true, characterData: true })
    window.addEventListener("resize", schedule)

    return () => {
      resizeObserver?.disconnect()
      mutationObserver.disconnect()
      window.removeEventListener("resize", schedule)
      if (raf) window.cancelAnimationFrame(raf)
    }
  }, [issueCount])

  const computeHeight = useCallback(() => {
    if (typeof window === "undefined") return
    const BOTTOM_GAP = BASE_BOTTOM_GAP + footerOffsetRef.current
    const effectiveTop = Math.min(topRef.current, STICKY_TOP)
    const available = window.innerHeight - effectiveTop - BOTTOM_GAP
    const rawAvailable = window.innerHeight - topRef.current - BOTTOM_GAP
    const next = Math.max(minDesktopHeight, available)
    setDesktopHeight(next)
    setDesktopAvailable(rawAvailable)
  }, [STICKY_TOP, BASE_BOTTOM_GAP, minDesktopHeight])

  useEffect(() => {
    if (typeof window === "undefined") return
    computeHeight()
    window.addEventListener("resize", computeHeight)
    return () => window.removeEventListener("resize", computeHeight)
  }, [computeHeight])

  useEffect(() => {
    if (typeof window === "undefined") return
    const footer = document.querySelector<HTMLElement>("footer")
    if (!footer) return
    let raf = 0

    const measure = () => {
      const rect = footer.getBoundingClientRect()
      const visibleHeight = Math.max(0, Math.min(rect.height, window.innerHeight - rect.top))
      const nextOffset = Math.min(FOOTER_PUSH_MAX, visibleHeight)
      if (Math.abs(nextOffset - footerOffsetRef.current) > 0.5) {
        footerOffsetRef.current = nextOffset
        computeHeight()
      }
    }

    const schedule = () => {
      if (raf) return
      raf = window.requestAnimationFrame(() => {
        raf = 0
        measure()
      })
    }

    measure()
    window.addEventListener("scroll", schedule, { passive: true })
    window.addEventListener("resize", schedule)
    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null
    resizeObserver?.observe(footer)

    return () => {
      window.removeEventListener("scroll", schedule)
      window.removeEventListener("resize", schedule)
      resizeObserver?.disconnect()
      if (raf) window.cancelAnimationFrame(raf)
    }
  }, [computeHeight])

  useEffect(() => {
    topRef.current = top
    if (!recomputedAfterTopChange.current && Math.abs(top - initialTopRef.current) > 0.5) {
      recomputedAfterTopChange.current = true
    }
    computeHeight()
  }, [top, computeHeight])

  useEffect(() => {
    if (desktopAvailable === null) return
    console.info("[Widget] scroll mode", {
      needsDesktopScroll,
      isStickyReady,
      available: Math.round(desktopAvailable),
      minHeight: minDesktopHeight
    })
  }, [needsDesktopScroll, desktopAvailable, minDesktopHeight])

  useEffect(() => {
    if (!needsDesktopScroll) {
      setDesktopInView(true)
      return
    }
    if (typeof window === "undefined") return
    let raf = 0
    let ticking = false

    const syncVisibility = () => {
      ticking = false
      const node = desktopWidgetRef.current
      if (!node) return
      const rect = node.getBoundingClientRect()
      const inView = rect.bottom > 0 && rect.top < window.innerHeight
      setDesktopInView(inView)
    }

    const schedule = () => {
      if (ticking) return
      ticking = true
      raf = window.requestAnimationFrame(syncVisibility)
    }

    syncVisibility()
    window.addEventListener("scroll", schedule, { passive: true })
    window.addEventListener("resize", schedule)

    return () => {
      window.removeEventListener("scroll", schedule)
      window.removeEventListener("resize", schedule)
      if (raf) window.cancelAnimationFrame(raf)
    }
  }, [needsDesktopScroll])

  const handleDesktopJump = useCallback(() => {
    const node = desktopWidgetRef.current
    if (!node || typeof window === "undefined") return
    const rect = node.getBoundingClientRect()
    const targetTop = rect.top + window.scrollY - 80
    window.scrollTo({ top: Math.max(0, targetTop), behavior: "smooth" })
  }, [])

  useEffect(() => {
    if (needsDesktopScroll) {
      if (unstickTopRef.current === null) {
        unstickTopRef.current = documentTop
      }
      return
    }
    unstickTopRef.current = null
  }, [needsDesktopScroll, documentTop])

  useEffect(() => {
    if (typeof window === "undefined") return
    const media = window.matchMedia?.("(prefers-color-scheme: dark)")
    if (!media) return
    const handleChange = (event: MediaQueryListEvent) => setSystemPrefersDark(event.matches)
    setSystemPrefersDark(media.matches)
    if (media.addEventListener) media.addEventListener("change", handleChange)
    else media.addListener(handleChange)
    return () => {
      if (media.removeEventListener) media.removeEventListener("change", handleChange)
      else media.removeListener(handleChange)
    }
  }, [])

  useEffect(() => {
    if (typeof document === "undefined") return
    if (!mobileOpen) return
    const { style } = document.body
    const prevOverflow = style.overflow
    const prevPaddingRight = style.paddingRight
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
    style.overflow = "hidden"
    if (scrollbarWidth > 0) {
      style.paddingRight = `${scrollbarWidth}px`
    }
    return () => {
      style.overflow = prevOverflow
      style.paddingRight = prevPaddingRight
    }
  }, [mobileOpen])

  const handleIssueClick = useCallback(
    (field: string) => {
      if (typeof document === "undefined") return
      const scrollToField = () => {
        const target = document.getElementById(`field-${field}`)
        if (!target) return
        target.scrollIntoView({ behavior: "smooth", block: "center" })
        const focusTarget = target.querySelector<HTMLElement>(
          "input, textarea, select, button, [role='radio'], [role='checkbox']"
        )
        focusTarget?.focus({ preventScroll: true })
      }

      if (mobileOpen) {
        setMobileOpen(false)
        window.setTimeout(scrollToField, 200)
        return
      }

      scrollToField()
    },
    [mobileOpen]
  )

  return (
    <>
      <div
        className={`hidden lg:flex ${needsDesktopScroll ? "absolute" : "fixed"} w-[480px] flex-col gap-6 overflow-hidden rounded-2xl border-slate-200/80 bg-muted/10 p-6 shadow-xl backdrop-blur-md transition-[opacity,transform] duration-300 ease-out ${
          widgetPositionMeasured && visible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0"
        }`}
        data-widget-container
        ref={desktopWidgetRef}
        style={{
          left: "calc(50% + 106px + 32px)",
          top: desktopTop,
          zIndex: 0,
          height: issueCount > 0 ? undefined : desktopHeight ?? 480,
          minHeight: issueCount > 0 ? undefined : minDesktopHeight,
          maxHeight: needsDesktopScroll || issueCount > 0 ? undefined : desktopAvailable ?? undefined
        }}
      >
        {issueCount > 0 ? (
          <WidgetIssues issues={issues} baseBorder={baseBorder} onIssueClick={handleIssueClick} />
        ) : (
          <WidgetContent
            logos={logos}
            licenseName={licenseName}
            licenseCode={licenseFullCode}
            baseBorder={baseBorder}
            accordion={accordion}
            setAccordion={setAccordion}
            sections={sections}
            menus={menus}
            onReset={onReset}
            widgetDownloadOptions={widgetDownloadOptions}
            onWidgetDownload={download}
          />
        )}
      </div>

      {needsDesktopScroll && (
        <div
          className={`hidden lg:block fixed bottom-4 right-4 z-30 transition-all duration-200 ${
            desktopInView ? "pointer-events-none translate-y-2 opacity-0" : "opacity-100 translate-y-0"
          }`}
        >
          <Button size="lg" className="shadow-lg" onClick={handleDesktopJump}>
            <FileText className="mr-2 size-5" />
            {issueCount > 0 ? (
              <span className="flex items-center gap-2">
                View checklist
                <span className="inline-flex size-5 items-center justify-center rounded-full bg-emerald-600 text-xs font-semibold text-white">
                  {issueCount}
                </span>
              </span>
            ) : (
              "Get your license"
            )}
          </Button>
        </div>
      )}

      <div className="lg:hidden fixed bottom-4 right-4 z-30">
        <Button size="lg" className="shadow-lg" onClick={() => setMobileOpen(true)}>
          <FileText className="mr-2 size-5" />
          {issueCount > 0 ? (
            <span className="flex items-center gap-2">
              View checklist
              <span className="inline-flex size-5 items-center justify-center rounded-full bg-emerald-600 text-xs font-semibold text-white">
                {issueCount}
              </span>
            </span>
          ) : (
            "Get your license"
          )}
        </Button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-[2] bg-white">
          <div className="fixed right-3 top-3 z-[3]">
            <Button size="icon" variant="ghost" onClick={() => setMobileOpen(false)}>
              <X className="size-5" />
            </Button>
          </div>
          <div className="flex h-full flex-col gap-6 overflow-auto bg-muted/10 p-5 pt-14" data-widget-container>
            {issueCount > 0 ? (
              <WidgetIssues issues={issues} baseBorder={baseBorder} onIssueClick={handleIssueClick} />
            ) : (
              <WidgetContent
                logos={logos}
                licenseName={licenseName}
                licenseCode={licenseFullCode}
                baseBorder={baseBorder}
                accordion={accordion}
                setAccordion={setAccordion}
                sections={sections}
                menus={menus}
                onReset={onReset}
                widgetDownloadOptions={widgetDownloadOptions}
                onWidgetDownload={download}
                allowContentStretch
              />
            )}
          </div>
        </div>
      )}
    </>
  )
}
